const STORAGE_KEY = 'theme';
type ThemeMode = 'light' | 'dark' | 'system';

export class Theme {
    private _current: ThemeMode = 'system';
    private _mediaQuery: MediaQueryList;
    private _mediaListener: ((e: MediaQueryListEvent) => void) | null = null;

    constructor() {
        this._mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this._load();
        this._apply();
        this._watchSystem();
    }

    /** 현재 설정된 테마 모드 */
    get current(): ThemeMode {
        return this._current;
    }

    /** 실제 적용 중인 테마 (light | dark) */
    get resolved(): 'light' | 'dark' {
        if (this._current === 'system') {
            return this._mediaQuery.matches ? 'dark' : 'light';
        }
        return this._current;
    }

    /** 다크 모드 활성 여부 */
    get isDark(): boolean {
        return this.resolved === 'dark';
    }

    /** 테마 설정 (light / dark / system) */
    public set(mode: ThemeMode) {
        this._current = mode;
        this._save();
        this._apply();
    }

    /** light ↔ dark 토글 (system 무시, 현재 resolved 기준 반전) */
    public toggle() {
        this.set(this.resolved === 'dark' ? 'light' : 'dark');
    }

    /** 정리 (컴포넌트 파괴 시 호출 가능) */
    public destroy() {
        if (this._mediaListener) {
            this._mediaQuery.removeEventListener('change', this._mediaListener);
            this._mediaListener = null;
        }
    }

    private _load() {
        const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
        if (stored && ['light', 'dark', 'system'].includes(stored)) {
            this._current = stored;
        }
    }

    private _save() {
        localStorage.setItem(STORAGE_KEY, this._current);
    }

    private _apply() {
        const html = document.documentElement;
        if (this.resolved === 'dark') {
            html.classList.add('dark');
        } else {
            html.classList.remove('dark');
        }
    }

    /** system 모드일 때 OS 설정 변경 감지 */
    private _watchSystem() {
        this._mediaListener = () => {
            if (this._current === 'system') {
                this._apply();
            }
        };
        this._mediaQuery.addEventListener('change', this._mediaListener);
    }
}

export default Theme;
